import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Store } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { CaptchaBox, CAPTCHA_ENABLED } from "@/components/CaptchaBox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", city: "", investment: "", message: "" };

export const FranchiseDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");
  const captchaRef = useRef(null);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email, and a short message.");
      return;
    }
    if (CAPTCHA_ENABLED && !token) {
      toast.error("Please complete the CAPTCHA below.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/franchising`, { ...form, turnstile_token: token });
      toast.success("Thanks for your interest! Our team will reach out about franchising soon. 🍩");
      setForm(empty);
      onOpenChange(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      captchaRef.current?.reset();
      setToken("");
    }
  };

  const field =
    "w-full rounded-2xl border-2 border-[#e8dcc8] bg-[#f7f0eb] px-4 py-3 text-[#111111] placeholder-[#3d3d3d]/50 focus:outline-none focus:border-[#ff648b] focus:ring-2 focus:ring-[#ff648b]/40 transition";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="franchise-dialog"
        className="sm:max-w-lg bg-white border-2 border-[#ff648b]/40 rounded-3xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="w-12 h-12 rounded-2xl bg-[#ff648b] flex items-center justify-center mb-2">
            <Store className="h-6 w-6 text-[#111111]" />
          </div>
          <DialogTitle className="font-display font-bold text-2xl text-[#111111]">
            Franchise with Bobo's
          </DialogTitle>
          <DialogDescription className="text-[#3d3d3d]">
            Want to bring Bobo's Donuts™ — and our mission of inclusion — to your
            community? Tell us a bit about you and we'll be in touch.
          </DialogDescription>
        </DialogHeader>

        <form data-testid="franchise-form" onSubmit={submit} className="space-y-4 mt-2">
          <input data-testid="franchise-name" name="name" value={form.name} onChange={change} placeholder="Full name *" className={field} />
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="franchise-email" name="email" type="email" value={form.email} onChange={change} placeholder="Email *" className={field} />
            <input data-testid="franchise-phone" name="phone" value={form.phone} onChange={change} placeholder="Phone" className={field} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="franchise-city" name="city" value={form.city} onChange={change} placeholder="Target city / region" className={field} />
            <select data-testid="franchise-investment" name="investment" value={form.investment} onChange={change} className={field}>
              <option value="">Investment range…</option>
              <option value="Under $50k">Under $50k</option>
              <option value="$50k – $100k">$50k – $100k</option>
              <option value="$100k – $250k">$100k – $250k</option>
              <option value="$250k+">$250k+</option>
            </select>
          </div>
          <textarea data-testid="franchise-message" name="message" value={form.message} onChange={change} placeholder="Tell us about your background & why Bobo's *" rows={3} className={field} />
          <CaptchaBox ref={captchaRef} onToken={setToken} />
          <button
            data-testid="franchise-submit-btn"
            type="submit"
            disabled={loading || (CAPTCHA_ENABLED && !token)}
            className="w-full py-3.5 rounded-full bg-[#111111] text-[#f7f0eb] font-bold text-lg hover:bg-[#000000] hover:scale-[1.02] transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            {loading ? "Sending..." : "Submit Inquiry"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
